/* eslint-disable react/no-danger */
/* eslint-disable react/no-did-update-set-state, import/no-named-as-default */

// node modules
import React, { Component, Suspense } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import classNames from 'classnames';
import { connect } from 'react-redux';
import NoSsr from '@material-ui/core/NoSsr';
import get from 'lodash/get';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import { Button } from '@material-ui/core';

// app modules: actions
import { searchIsActive, loadSearchView } from 'actions/search';
import { openAvatarMenu, closeAvatarMenu } from 'actions/avatarMenu';

// app modules: components
import Hamburger from 'components/Hamburger';
import HomeConnectMenu from 'components/HomeConnectMenu/LazyHomeConnectMenu';
import Link from 'components/Link';
import NotificationButton from 'components/NotificationButton';
import SearchBoxCustom from 'components/SearchBoxCustom';
import SearchProvider from 'components/SearchProvider';
import UserMenu from 'components/UserMenu';
import PinnedPages from 'components/PinnedPages';
import Icon from 'components/Icon';
import HideOnScroll from 'components/HideOnScroll';
import SearchFilters from 'components/SearchFilters';

// app modules: services
import history from 'services/history';
import { isApp as isAppHelper } from 'services/environment';

// constants
import { OPERATOR, MIN_SEARCH_LENGTH } from 'constants/search';
import { BREAKPOINTS, COLOURS } from 'constants/index';

// assets
import CssLayoutHeader from './LayoutHeader.scss';

const QUERY_OPTIONS = { default_operator: OPERATOR };

const SearchResultsLayout = React.lazy(() =>
  import(/* webpackChunkName: 'search-results' */ './SearchResultsLayout'),
);

class LayoutHeader extends Component {
  static propTypes = {
    className: PropTypes.string,
    bodyWidth: PropTypes.number,
    displayType: PropTypes.string.isRequired,
    router: PropTypes.shape({
      pathname: PropTypes.string,
      state: PropTypes.string,
    }).isRequired,
    search: PropTypes.shape({
      isEmpty: PropTypes.bool,
      isLoading: PropTypes.bool,
      isActive: PropTypes.bool,
      searchViewRequired: PropTypes.bool,
    }).isRequired,
    searchIsActive: PropTypes.func.isRequired,
    loadSearchView: PropTypes.func.isRequired,
    avatarMenuVisible: PropTypes.bool.isRequired,
    openAvatarMenu: PropTypes.func.isRequired,
    closeAvatarMenu: PropTypes.func.isRequired,
    onPinToggleUpdated: PropTypes.func,
    pinnedPagesOpen: PropTypes.bool,
    homeConnectEnabled: PropTypes.bool.isRequired,
    pinnedPages: PropTypes.objectOf(
      PropTypes.shape({
        title: PropTypes.string,
        book: PropTypes.shape({
          image_name: PropTypes.string,
        }),
        primary_image: PropTypes.shape({
          crops: PropTypes.arrayOf(
            PropTypes.shape({
              sizes: PropTypes.arrayOf(
                PropTypes.shape({
                  src: PropTypes.string,
                }),
              ),
            }),
          ),
        }),
      }),
    ),
  };

  static defaultProps = {
    className: null,
    bodyWidth: null,
    onPinToggleUpdated: null,
    pinnedPagesOpen: true,
    pinnedPages: {},
  };

  constructor(props) {
    super(props);

    this.bound = {
      onResultsChange: this.onResultsChange.bind(this),
      onSearchBoxFocus: this.onSearchBoxFocus.bind(this),
      onSearchBoxBlur: this.onSearchBoxBlur.bind(this),
      onSearchBoxChange: this.onSearchBoxChange.bind(this),
      handleAvatarMenuClick: this.handleAvatarMenuClick.bind(this),
      handleTogglePinnedPages: this.handleTogglePinnedPages.bind(this),
      handleOnHideTrigger: this.handleOnHideTrigger.bind(this),
      updateHeaderContentHeight: this.updateHeaderContentHeight.bind(this),
      handleSearchFiltersToggle: this.handleSearchFiltersToggle.bind(this),
    };

    this.state = {
      isSearchView: false,
      searchBoxFocused: false,
      searchDirty: false,
      isHiddenOnScroll: false,
      headerContentHeight: 0,
    };

    // Hidden feature - full text search for experimental purposes. Don't link to this because it will be removed later.
    this.searchFields = []; // search fields now controlled by the server
    if (history.location.pathname === '/search/fulltext') {
      this.searchFields.push('_fulltext');
    }

    this.searchResultsRef = React.createRef();
    this.hideOnScrollRef = React.createRef();
    this.headerContentRef = React.createRef();
  }

  componentDidMount() {
    setTimeout(() => {
      this.updateHeaderContentHeight();
    }, 350);
  }

  // eslint-disable-next-line no-unused-vars
  componentDidUpdate(prevProps, _prevState, _snapshot) {
    if (this.props.search.isActive && !this.props.search.searchViewRequired) {
      this.props.loadSearchView();
    }

    let isSearchView = null;
    // location has changed
    if (prevProps.router.state === 'loading' && this.props.router.state === 'loaded') {
      document.body.style.overflow = '';
      isSearchView = history.location.pathname.startsWith('/search');
      this.setState({
        isSearchView,
      });

      if (this.props.search.isActive !== isSearchView) {
        this.props.searchIsActive(isSearchView);
      }
    }

    if (prevProps.search.isActive !== this.props.search.isActive) {
      this.setState({
        listenOnSearchScroll: this.props.search.isActive || isSearchView,
      });
    }

    // Update header height when pinned pages toggle
    if (prevProps.pinnedPagesOpen !== this.props.pinnedPagesOpen) {
      // Delay to allow CSS transitions to complete
      setTimeout(() => {
        this.updateHeaderContentHeight();
      }, 350);
    }
  }

  onResultsChange(results, searchkit) {
    const { isEmpty } = this.props.search;
    const { searchDirty, searchBoxFocused } = this.state;

    // A preference change re-runs the search in the background. Off the search route, with the
    // search box idle and no search view open, that must not pull the reader out of the page.
    if (
      !history.location.pathname.startsWith('/search') &&
      !searchBoxFocused &&
      !this.props.search.isActive
    ) {
      return;
    }

    document.body.style.overflow = isEmpty ? '' : 'hidden'; // enable / disable body scrolling.
    const isSearchView = !isEmpty || history.location.pathname.startsWith('/search');
    const isPageLoad = get(searchkit, 'accessors.statefulAccessors.p.state.value', 0) > 0;

    if (!isPageLoad) {
      this.setState({ isSearchView });
      if (this.props.search.isActive !== isSearchView) {
        this.props.searchIsActive(isSearchView);
      }
    }
    if (this.searchResultsRef.current && searchDirty) {
      this.searchResultsRef.current.scrollTop = 0;
      this.setState({ searchDirty: false });
    }
  }

  onSearchBoxFocus() {
    const { isEmpty } = this.props.search;
    this.setState({
      isSearchView: !isEmpty,
      searchBoxFocused: true,
    });
    this.props.searchIsActive(!isEmpty);

    this.props.loadSearchView();
  }

  onSearchBoxBlur() {
    this.setState({ searchBoxFocused: false });
  }

  onSearchBoxChange() {
    this.setState({ searchDirty: true });
  }

  handleAvatarMenuClick() {
    const { avatarMenuVisible } = this.props;
    if (avatarMenuVisible) {
      this.props.closeAvatarMenu();
    } else {
      this.props.openAvatarMenu();
    }
  }

  linkClassName(pathname) {
    const { router } = this.props;

    return classNames('layout-header__menu__link layout-header__menu__link--shifted', {
      'layout-header__menu__link--active': router.pathname.indexOf(pathname) === 0,
    });
  }

  handleTogglePinnedPages = pinState => {
    if (pinState !== null && this.props.onPinToggleUpdated) {
      this.props.onPinToggleUpdated();
    }

    if (this.hideOnScrollRef.current) {
      this.hideOnScrollRef.current.resetVisibility();
    }

    // Update header height after pinned pages animation completes
    setTimeout(() => {
      this.updateHeaderContentHeight();
    }, 350);
  };

  updateHeaderContentHeight() {
    if (this.headerContentRef.current) {
      const height = this.headerContentRef.current.offsetHeight;
      this.setState({ headerContentHeight: height });
    }
  }

  handleSearchFiltersToggle() {
    // Update header height after search filters animation completes
    setTimeout(() => {
      this.updateHeaderContentHeight();
    }, 350);
  }

  handleOnHideTrigger(hiddenState) {
    const {
      search: { isActive },
    } = this.props;
    this.setState(prevState => ({
      isHiddenOnScroll: hiddenState,
      isHeaderHidden: hiddenState && !prevState.isSearchView && !isActive,
    }));
  }

  renderPlaceholder() {
    const { displayType } = this.props;

    if (displayType !== 'desktop') {
      return 'Ingredients, dishes, authors, books...';
    }

    return 'Search ingredients, dishes, authors, books and more...';
  }

  render() {
    const {
      search,
      homeConnectEnabled,
      className,
      pinnedPages,
      pinnedPagesOpen,
      bodyWidth,
    } = this.props;
    const {
      isSearchView,
      isHeaderHidden,
      listenOnSearchScroll,
      isHiddenOnScroll,
      headerContentHeight,
    } = this.state;
    const searchOnLoad = history.location.pathname.startsWith('/search');

    const hasPinnedPages = Object.values(pinnedPages).some(value => value);

    const layoutHeaderClassName = classNames('layout-header', className, {
      'layout-header--search-active': isSearchView,
      'layout-header--is-hidden': isHeaderHidden,
    });

    const linksClassName = {
      authors: this.linkClassName('/authors'),
      books: this.linkClassName('/book'),
      collections: this.linkClassName('/collection'),
      features: this.linkClassName('/features'),
    };

    // Apply dynamic negative margin to search results when header is hidden on scroll
    const searchResultsStyle =
      isSearchView && isHiddenOnScroll && headerContentHeight > 0
        ? { marginTop: `-${headerContentHeight}px` }
        : {};

    const isApp = !!isAppHelper();

    return (
      <SearchProvider
        allowEmptySearch
        minimumSearchLength={MIN_SEARCH_LENGTH}
        onResultsChange={this.bound.onResultsChange}
        searchBoxFocused={this.state.searchBoxFocused}
        // Don't trigger the search yet - it will be fired from the effect after SearchResultsView has loaded.
        searchOnLoad={false}
      >
        <div className={layoutHeaderClassName}>
          <HideOnScroll
            enabled={isApp}
            ref={this.hideOnScrollRef}
            onHideTrigger={this.bound.handleOnHideTrigger}
            listenElementSelector={listenOnSearchScroll ? '.layout-header__search-results' : null}
          >
            <div className="layout-header__content" ref={this.headerContentRef}>
              <div className="layout-header__wrapper">
                <Hamburger />
                <Link to="/" className="layout-header__logo">
                  <img src="images/logo.png" className="layout-header__logo__image" alt="Logo" />
                </Link>
                <SearchBoxCustom
                  searchOnChange
                  queryFields={this.searchFields}
                  queryOptions={QUERY_OPTIONS}
                  placeholder={this.renderPlaceholder()}
                  onFocus={this.bound.onSearchBoxFocus}
                  onBlur={this.bound.onSearchBoxBlur}
                  onChange={this.bound.onSearchBoxChange}
                  autoFocus={false}
                />

                <nav className="layout-header__navigation">
                  <ul className="layout-header__menu layout-header__menu--main">
                    <li className="layout-header__menu__item">
                      <Link to="/authors" className={linksClassName.authors}>
                        Authors
                      </Link>
                    </li>
                    <li className="layout-header__menu__item">
                      <Link to="/books" className={linksClassName.books}>
                        Books
                      </Link>
                    </li>

                    <li className="layout-header__menu__item">
                      <Link to="/features" className={linksClassName.features}>
                        Features
                      </Link>
                    </li>

                    <li className="layout-header__menu__item">
                      <Link to="/collections" className={linksClassName.collections}>
                        Collections
                      </Link>
                    </li>
                  </ul>

                  <ul className="layout-header__menu layout-header__menu--oven">
                    <NoSsr defer>
                      {homeConnectEnabled ? (
                        <li className="layout-header__menu__item">
                          <HomeConnectMenu disableSwipeToOpen className="layout-header__hc" />
                        </li>
                      ) : null}
                    </NoSsr>
                    <li className="layout-header__menu__item">
                      <NotificationButton />
                    </li>
                  </ul>

                  <ul className="layout-header__menu layout-header__menu--side">
                    <li className="layout-header__menu__item">
                      <UserMenu loginLinkClassName="layout-header__menu__link layout-header__menu__link--shifted" />
                    </li>
                  </ul>
                </nav>
              </div>
              {hasPinnedPages ? (
                <>
                  <div className="layout-header__pinned-pages__wrapper">
                    <div
                      tabIndex={0}
                      role="button"
                      onClick={pinnedPagesOpen ? undefined : this.bound.handleTogglePinnedPages}
                      onKeyDown={e => {
                        if (!pinnedPagesOpen && (e.key === 'Enter' || e.key === ' ')) {
                          this.bound.handleTogglePinnedPages();
                        }
                      }}
                      className={`layout-header__pinned-pages__container ${
                        pinnedPagesOpen
                          ? 'layout-header__pinned-pages__container--open'
                          : 'layout-header__pinned-pages__container--closed'
                      }`}
                    >
                      <PinnedPages
                        className={`layout-header__pinned-pages ${
                          pinnedPagesOpen ? 'pinned-pages--visible' : 'pinned-pages--hidden'
                        }`}
                        isPinnedPagesOpen={pinnedPagesOpen}
                        togglePinnedPages={this.bound.handleTogglePinnedPages}
                        pinnedPages={pinnedPages}
                      />

                      <div
                        className={`layout-header__pinned-pages__indicator-icon ${
                          pinnedPagesOpen
                            ? 'layout-header__pinned-pages__indicator-icon--hidden'
                            : 'layout-header__pinned-pages__indicator-icon--visible'
                        }`}
                      >
                        <Icon
                          className="layout-header__pinned-pages__indicator-icon__icon"
                          fill={COLOURS.SOLID_GREY}
                          name="push-pin-filled"
                          height="25"
                          width="25"
                        />
                      </div>

                      <Button
                        size="small"
                        onClick={pinnedPagesOpen ? this.bound.handleTogglePinnedPages : undefined}
                        className={`layout-header__pinned-pages__dropdown-button ${
                          pinnedPagesOpen
                            ? 'layout-header__pinned-pages__dropdown-button--open'
                            : 'layout-header__pinned-pages__dropdown-button--closed'
                        }`}
                      >
                        <ChevronRightIcon
                          fontSize="medium"
                          style={{
                            transform: pinnedPagesOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                          }}
                          className={`layout-header__pinned-pages__dropdown-button__icon ${
                            pinnedPagesOpen
                              ? ''
                              : 'layout-header__pinned-pages__dropdown-button__icon--closed'
                          }`}
                        />
                      </Button>
                    </div>
                  </div>

                  {/* <div
                    role="button"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        this.bound.handleTogglePinnedPages();
                      }
                    }}
                    tabIndex={0}
                    onClick={this.bound.handleTogglePinnedPages}
                    className="layout-header__pinned-pages__toggle-pins-desktop"
                  >
                    <Button
                      size="medium"
                      className={`layout-header__pinned-pages__toggle-button ${
                        pinnedPagesOpen
                          ? 'layout-header__pinned-pages__toggle-button--open'
                          : 'layout-header__pinned-pages__toggle-button--closed'
                      }`}
                    >
                      Pinned recipes
                      <ChevronRightIcon
                        fontSize="medium"
                        style={{
                          transform: pinnedPagesOpen ? 'rotate(-90deg)' : 'rotate(90deg)',
                        }}
                        className={`layout-header__pinned-pages__toggle-button__icon ${
                          pinnedPagesOpen
                            ? ''
                            : 'layout-header__pinned-pages__toggle-button__icon--closed'
                        }`}
                      />
                    </Button>
                  </div> */}
                </>
              ) : null}
              {/* Only renders in mobile view to keep search filters scrollable */}
              {bodyWidth <= BREAKPOINTS.PHONE_VERTICAL ? (
                <SearchFilters
                  isVisible={isSearchView}
                  onToggle={this.bound.handleSearchFiltersToggle}
                />
              ) : null}
            </div>
          </HideOnScroll>
          <NoSsr>
            {searchOnLoad || search.searchViewRequired ? (
              <Suspense fallback={null}>
                <SearchResultsLayout
                  visible={isSearchView}
                  ref={this.searchResultsRef}
                  style={searchResultsStyle}
                  onSearchFiltersToggle={this.bound.handleSearchFiltersToggle}
                />
              </Suspense>
            ) : null}
          </NoSsr>
        </div>
      </SearchProvider>
    );
  }
}

const mapStateToProps = state => ({
  displayType: state.dimensions.displayType,
  examples: state.search.examples.items,
  router: state.router,
  search: state.search,
  avatarMenuVisible: state.avatarMenu.isOpen,
  homeConnectEnabled: !!state.user.home_connect_enabled,
  pinnedPages: state.pinnedPages,
  bodyWidth: state.dimensions.body,
});

const mapDispatchToProps = {
  searchIsActive,
  openAvatarMenu,
  closeAvatarMenu,
  loadSearchView,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(CssLayoutHeader)(LayoutHeader));
